import { useState, useRef, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Camera, Upload, Loader2, Scale, Package, Building2, CheckCircle2, AlertTriangle, X } from "lucide-react";
import { toast } from "sonner";
import api from "../services/api";

const MAX_IMG_BYTES = 3_500_000; // Agent can upload images up to ~3.5MB (backend cap)

function readImageAsBase64(file) {
  return new Promise((resolve, reject) => {
    if (!file) return reject(new Error("No file"));
    if (!file.type.startsWith("image/")) return reject(new Error("Please upload an image"));
    if (file.size > MAX_IMG_BYTES) return reject(new Error("Image must be under 3.5 MB"));
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Couldn't read image"));
    reader.readAsDataURL(file);
  });
}

function PhotoPicker({ label, value, onChange, testId }) {
  const inputRef = useRef(null);
  const captureRef = useRef(null);
  return (
    <div className="space-y-2">
      <Label className="text-xs font-semibold text-slate-700">{label}</Label>
      {value ? (
        <div className="relative group">
          <img src={value} alt="preview" className="w-full h-40 object-cover rounded-xl border border-slate-200" data-testid={`${testId}-preview`} />
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-black"
            data-testid={`${testId}-remove`}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => captureRef.current?.click()}
            className="h-24 rounded-xl border-2 border-dashed border-[#FF6B00]/40 bg-orange-50/40 hover:bg-orange-50 flex flex-col items-center justify-center text-[#FF6B00] transition"
            data-testid={`${testId}-capture`}
          >
            <Camera className="h-5 w-5 mb-1" />
            <span className="text-[11px] font-semibold">Take Photo</span>
          </button>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="h-24 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 hover:bg-slate-100 flex flex-col items-center justify-center text-slate-600 transition"
            data-testid={`${testId}-upload`}
          >
            <Upload className="h-5 w-5 mb-1" />
            <span className="text-[11px] font-semibold">Upload</span>
          </button>
        </div>
      )}
      <input
        ref={captureRef}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={async (e) => {
          const f = e.target.files?.[0];
          if (!f) return;
          try { onChange(await readImageAsBase64(f)); }
          catch (err) { toast.error(err.message); }
          finally { e.target.value = ""; }
        }}
      />
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={async (e) => {
          const f = e.target.files?.[0];
          if (!f) return;
          try { onChange(await readImageAsBase64(f)); }
          catch (err) { toast.error(err.message); }
          finally { e.target.value = ""; }
        }}
      />
    </div>
  );
}

/**
 * AgentVerifyDialog
 * mode = "weight"  -> R7: Pickup weight + scale photo
 * mode = "drop"    -> R8: Branch drop-off confirmation (receipt + optional barcode)
 */
export default function AgentVerifyDialog({ open, onOpenChange, mode, order, onDone }) {
  const [submitting, setSubmitting] = useState(false);

  // weight mode state
  const [measuredWeight, setMeasuredWeight] = useState("");
  const [scalePhoto, setScalePhoto] = useState("");
  const [weightNotes, setWeightNotes] = useState("");

  // drop mode state
  const [branchName, setBranchName] = useState("");
  const [receiptPhoto, setReceiptPhoto] = useState("");
  const [packagePhoto, setPackagePhoto] = useState("");
  const [awb, setAwb] = useState("");
  const [dropNotes, setDropNotes] = useState("");
  // handover-target auto-fill
  const [selectedHubId, setSelectedHubId] = useState("");
  const [approvedHubs, setApprovedHubs] = useState([]);
  const [autoDetected, setAutoDetected] = useState(false);
  const [generatedOtp, setGeneratedOtp] = useState("");

  // Auto-fill the assigned handover hub + tracking when the drop dialog opens.
  useEffect(() => {
    if (!open || mode !== "drop" || !order?.tracking_id) return;
    setGeneratedOtp("");
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/orders/${order.tracking_id}/handover-target`);
        if (cancelled) return;
        setApprovedHubs(data.approved_hubs || []);
        setAutoDetected(!!data.auto_detected);
        if (data.suggested_hub) {
          setSelectedHubId(data.suggested_hub.hub_id);
          setBranchName(data.suggested_hub.name || data.suggested_hub.hub_id);
        }
        if (data.awb) setAwb(data.awb);
        // If a handover is already pending, re-show its OTP to the agent.
        if (data.pending_handover && data.handover_otp) setGeneratedOtp(data.handover_otp);
      } catch {
        /* auto-detect failed — agent can pick a hub manually */
      }
    })();
    return () => { cancelled = true; };
  }, [open, mode, order?.tracking_id]);

  const reset = () => {
    setMeasuredWeight(""); setScalePhoto(""); setWeightNotes("");
    setBranchName(""); setReceiptPhoto(""); setPackagePhoto(""); setAwb(""); setDropNotes("");
    setSelectedHubId(""); setApprovedHubs([]); setAutoDetected(false); setGeneratedOtp("");
  };

  const handleClose = (o) => {
    if (!o) reset();
    onOpenChange(o);
  };

  const submitWeight = async () => {
    const w = parseFloat(measuredWeight);
    if (!w || w <= 0) return toast.error("Enter a valid measured weight");
    if (!scalePhoto) return toast.error("Scale photo is required");
    setSubmitting(true);
    try {
      const { data } = await api.post("/orders/verify/pickup-weight", {
        tracking_id: order.tracking_id,
        measured_weight_kg: w,
        scale_photo_base64: scalePhoto,
        notes: weightNotes,
      });
      if (data.weight_mismatch) {
        toast.warning(`Weight verified — mismatch flagged (declared ${data.declared} kg, measured ${data.measured} kg)`);
      } else {
        toast.success("Weight verified");
      }
      onDone?.();
      handleClose(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't submit weight");
    } finally { setSubmitting(false); }
  };

  const submitDrop = async () => {
    const hubId = selectedHubId || "";
    const hubName = (approvedHubs.find((h) => h.hub_id === hubId)?.name) || branchName.trim();
    if (!hubId && !hubName) return toast.error("Select a Handover Hub / Branch");
    setSubmitting(true);
    try {
      const { data } = await api.post("/orders/verify/branch-drop", {
        tracking_id: order.tracking_id,
        handover_hub_id: hubId || null,
        branch_name: hubName || null,
        awb: awb.trim() || null,
        receipt_photo_base64: receiptPhoto || null,
        package_photo_base64: packagePhoto || null,
        notes: dropNotes,
      });
      // Show the generated OTP for the agent to share with hub staff —
      // do NOT close the dialog yet.
      setGeneratedOtp(data.handover_otp || "");
      toast.success("Handover recorded. Share the OTP with hub staff.");
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't submit handover");
    } finally { setSubmitting(false); }
  };

  const isWeight = mode === "weight";
  const declaredWeight = Number(order?.package_weight || 0);
  const mismatch = isWeight && measuredWeight && declaredWeight > 0 && Math.abs(parseFloat(measuredWeight) - declaredWeight) > Math.max(0.2, declaredWeight * 0.15);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md" data-testid="agent-verify-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] text-lg flex items-center gap-2">
            {isWeight ? <><Scale className="h-5 w-5 text-[#FF6B00]" /> Verify Package Weight</>
                      : <><Building2 className="h-5 w-5 text-[#FF6B00]" /> Handover to Hub</>}
          </DialogTitle>
        </DialogHeader>

        <div className="text-[11px] text-slate-500 -mt-1 mb-1">
          Tracking: <span className="font-mono font-semibold text-slate-700">{order?.tracking_id}</span>
        </div>

        {isWeight ? (
          <div className="space-y-3">
            <div className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs text-slate-600 flex items-center gap-2">
              <Package className="h-3.5 w-3.5 text-slate-400" />
              Declared weight: <span className="font-semibold text-slate-900">{declaredWeight > 0 ? `${declaredWeight} kg` : "Not declared"}</span>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-700">Measured weight (kg)</Label>
              <Input
                type="number" step="0.01" min="0.01" placeholder="e.g. 2.35"
                value={measuredWeight} onChange={(e) => setMeasuredWeight(e.target.value)}
                className="rounded-xl h-11" data-testid="verify-weight-input"
              />
            </div>
            {mismatch && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-xs text-amber-700 flex items-start gap-2" data-testid="weight-mismatch-warning">
                <AlertTriangle className="h-4 w-4 mt-0.5" />
                <span>Weight mismatch detected. This will be flagged to admin for review.</span>
              </div>
            )}
            <PhotoPicker label="Scale / weighing photo (required)" value={scalePhoto} onChange={setScalePhoto} testId="scale-photo" />
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-700">Notes (optional)</Label>
              <Input
                placeholder="e.g. packaging a bit damaged"
                value={weightNotes} onChange={(e) => setWeightNotes(e.target.value)}
                className="rounded-xl h-10" data-testid="weight-notes-input"
              />
            </div>
            <Button
              onClick={submitWeight} disabled={submitting}
              className="w-full bg-[#FF6B00] hover:bg-[#E65A00] rounded-xl h-11 mt-2"
              data-testid="submit-weight-btn"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-1.5" /> Submit Verification</>}
            </Button>
          </div>
        ) : generatedOtp ? (
          <div className="space-y-4 text-center py-2" data-testid="handover-otp-panel">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2 text-xs text-emerald-700">
              Handover recorded. Show this OTP to the <b>Micro Hub Lead / Main Hub Manager</b> — they verify it on their hub dashboard to receive the parcel.
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-500">Hub Handover OTP</Label>
              <div className="text-4xl font-extrabold tracking-[0.3em] text-[#FF6B00] mt-1" data-testid="handover-otp-value">{generatedOtp}</div>
              <p className="text-[11px] text-slate-400 mt-1">The customer does NOT receive this OTP.</p>
            </div>
            <Button onClick={() => handleClose(false)} className="w-full bg-[#FF6B00] hover:bg-[#E65A00] rounded-xl h-11" data-testid="handover-done-btn">
              Done
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-700">Handover Hub / Branch</Label>
              <select
                value={selectedHubId}
                onChange={(e) => { setSelectedHubId(e.target.value); const h = approvedHubs.find((x) => x.hub_id === e.target.value); setBranchName(h?.name || ""); }}
                className="w-full rounded-xl h-11 border border-slate-200 px-3 text-sm"
                data-testid="handover-hub-select"
              >
                {!selectedHubId && <option value="">Select a hub…</option>}
                {approvedHubs.map((h) => (
                  <option key={h.hub_id} value={h.hub_id}>
                    {h.name || h.hub_id} ({h.hub_type})
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-slate-400">
                {autoDetected ? "Auto-detected from the order route — change only if needed." : "Auto-detection unavailable — please select the hub."}
              </p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-700">Tracking ID (auto-filled)</Label>
              <Input value={order?.tracking_id || ""} readOnly
                className="rounded-xl h-11 font-mono tracking-wider bg-slate-50" data-testid="handover-tracking-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-700">Courier AWB (optional)</Label>
              <Input
                placeholder="Only if an external courier AWB exists"
                value={awb} onChange={(e) => setAwb(e.target.value)}
                className="rounded-xl h-11 font-mono tracking-wider" data-testid="branch-awb-input"
              />
            </div>
            <PhotoPicker label="Branch receipt photo (optional)" value={receiptPhoto} onChange={setReceiptPhoto} testId="receipt-photo" />
            <PhotoPicker label="Package photo (optional)" value={packagePhoto} onChange={setPackagePhoto} testId="package-photo" />
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-700">Notes (optional)</Label>
              <Input
                placeholder="Any handover details"
                value={dropNotes} onChange={(e) => setDropNotes(e.target.value)}
                className="rounded-xl h-10" data-testid="drop-notes-input"
              />
            </div>
            <Button
              onClick={submitDrop} disabled={submitting}
              className="w-full bg-[#FF6B00] hover:bg-[#E65A00] rounded-xl h-11 mt-2"
              data-testid="submit-drop-btn"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-1.5" /> Confirm Handed to Hub</>}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
